import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// "+ New" chooser: paste from clipboard vs. create blank. When the
// Clipboard API succeeds we show a preview snippet and make "Paste" the
// primary action. On rejection (permission denied, no API, page not focused)
// we fall back to a manual textarea.
import { useEffect, useRef, useState } from "react";
const SNIPPET_MAX = 200;
export function NewReadmeChooserModal({ open, onClose, onChooseBlank, onChoosePaste, }) {
    const [clipText, setClipText] = useState(null);
    const [clipChecked, setClipChecked] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [manualText, setManualText] = useState("");
    const textareaRef = useRef(null);
    useEffect(() => {
        if (!open) {
            setClipText(null);
            setClipChecked(false);
            setShowManual(false);
            setManualText("");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const text = await navigator.clipboard.readText();
                if (cancelled)
                    return;
                const trimmed = text.trim();
                setClipText(trimmed.length > 0 ? trimmed : null);
            }
            catch {
                if (!cancelled)
                    setClipText(null);
            }
            finally {
                if (!cancelled)
                    setClipChecked(true);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        function handleKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [open, onClose]);
    useEffect(() => {
        if (showManual) {
            window.requestAnimationFrame(() => textareaRef.current?.focus());
        }
    }, [showManual]);
    if (!open)
        return null;
    const snippet = clipText && clipText.length > SNIPPET_MAX
        ? `${clipText.slice(0, SNIPPET_MAX)}…`
        : clipText;
    function commitPasteFromClipboard() {
        if (clipText)
            onChoosePaste(clipText);
    }
    function commitManual() {
        const text = manualText.trim();
        if (text.length > 0)
            onChoosePaste(text);
    }
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center", onClick: onClose, children: _jsxs("div", { className: "card w-full max-w-md p-6", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "mb-4 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold", children: "New readme" }), _jsx("button", { type: "button", "aria-label": "Close", className: "rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800", onClick: onClose, children: "\u2715" })] }), !showManual && clipText && snippet && (_jsxs("div", { className: "mb-4", children: [_jsx("p", { className: "mb-1 text-xs font-medium uppercase tracking-wide text-slate-500", children: "Clipboard preview" }), _jsx("pre", { className: "max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300", children: snippet })] })), !showManual && !clipText && clipChecked && (_jsx("p", { className: "mb-4 text-sm text-slate-600 dark:text-slate-300", children: "No clipboard text detected. Create a blank readme, or paste content manually." })), !showManual && !clipChecked && (_jsx("p", { className: "mb-4 text-sm text-slate-500", children: "Checking clipboard\u2026" })), showManual && (_jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500", children: "Paste content" }), _jsx("textarea", { ref: textareaRef, className: "input min-h-[140px] w-full font-mono text-xs", placeholder: "Paste markdown or text here\u2026", value: manualText, onChange: (e) => setManualText(e.target.value) })] })), _jsx("div", { className: "flex flex-col gap-2 sm:flex-row sm:justify-end", children: showManual ? (_jsxs(_Fragment, { children: [_jsx("button", { className: "btn-secondary", onClick: () => { setShowManual(false); setManualText(""); }, children: "Back" }), _jsx("button", { className: "btn-primary", onClick: commitManual, disabled: manualText.trim().length === 0, children: "Use this text" })] })) : (_jsxs(_Fragment, { children: [_jsx("button", { className: "btn-secondary", onClick: onChooseBlank, children: "Create blank" }), clipText ? (_jsx("button", { className: "btn-primary", onClick: commitPasteFromClipboard, children: "Paste from clipboard" })) : (_jsx("button", { className: "btn-primary", onClick: () => setShowManual(true), disabled: !clipChecked, children: "Paste manually\u2026" }))] })) })] }) }));
}
